/**
 * ============================================================================
 * [P3-03-D] ScriptCard - 스크립트 카드 컴포넌트
 *
 * 기능:
 * - 단일 스크립트 카드 표시
 * - 듀얼 패널 선택 상태 표시 (📌 1 / 📌 2 배지)
 * - 클릭 이벤트 핸들링
 *
 * [DP-01] 듀얼 패널 고정 기능 추가
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { memo, useCallback } from 'react'
import type { Script, PanelIndex } from '@/types'
import styles from './ScriptCard.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface ScriptCardProps {
  script: Script
  panelSelection: PanelIndex | null  // null=미선택, 0=패널1, 1=패널2
  onSelect: (script: Script) => void
}

// ============================================================================
// 3. ScriptCard Component (memo로 최적화)
// ============================================================================
export const ScriptCard = memo(function ScriptCard({
  script,
  panelSelection,
  onSelect,
}: ScriptCardProps) {
  // ========================================================================
  // 3-1. 클릭 핸들러
  // ========================================================================
  const handleClick = useCallback(() => {
    onSelect(script)
  }, [script, onSelect])

  // ========================================================================
  // 3-2. 날짜 포맷 함수
  // ========================================================================
  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // ========================================================================
  // 3-3. CSS 클래스 결정
  // ========================================================================
  const cardClassName = `${styles.scriptCard} ${
    panelSelection === 0 ? styles.selectedPanel1 :
    panelSelection === 1 ? styles.selectedPanel2 : ''
  }`

  // ========================================================================
  // 3-4. Render
  // ========================================================================
  return (
    <div
      className={cardClassName}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      {/* 패널 선택 배지 */}
      {panelSelection !== null && (
        <span className={panelSelection === 0 ? styles.pinBadge1 : styles.pinBadge2}>
          📌 {panelSelection + 1}
        </span>
      )}

      {/* 스크립트 이름 */}
      <h4>{script.name || '이름 없음'}</h4>

      {/* 스크립트 내용 미리보기 (100자) */}
      <p className={styles.scriptPreview}>
        {script.content.length > 100
          ? `${script.content.slice(0, 100)}...`
          : script.content || '내용 없음'}
      </p>

      {/* 메타데이터 (날짜, 글자수, 카테고리) */}
      <div className={styles.scriptMeta}>
        <span className={styles.scriptMetaItem}>
          📅 {formatDate(script.updatedAt)}
        </span>
        <span className={styles.scriptMetaItem}>
          📝 {script.content.length}자
        </span>
        {script.category && (
          <span className={styles.scriptMetaItem}>
            📁 {script.category}
          </span>
        )}
      </div>
    </div>
  )
})

export default ScriptCard
